import { useState } from 'react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import {
  Badge,
  Button,
  ConfirmDialog,
  DataTable,
  EmptyState,
  PageBody,
  Sparkline,
  TableActionsMenu,
  Tooltip,
  type DataTableColumn,
  type DataTableSort,
} from '@wundo/ui';
import { PageHeader } from '@/components/PageHeader.tsx';
import { SkillFlags } from '@/components/skills/SkillFlags.tsx';
import { deleteSkill, listSkills, type Skill } from '@/lib/skills.ts';
import { PAGE_SIZE } from '@/lib/pageSize.ts';
import { apiErrorMessage } from '@/lib/apiErrorMessage.ts';
import { reportError } from '@/lib/reportError.ts';

export const Route = createFileRoute('/_app/skills/')({
  staticData: { orgScope: 'none' },
  component: SkillsRoute,
});

function SkillsRoute() {
  return <SkillsPage />;
}

const columns: DataTableColumn<Skill>[] = [
  {
    key: 'name',
    header: 'Name',
    sortable: true,
    render: (skill) => (
      <span className='font-mono text-sm font-semibold max-mobile:wrap-anywhere'>
        {skill.name}
      </span>
    ),
  },
  {
    key: 'visibility',
    header: 'Visibility',
    hideBelow: 'mobile',
    nowrap: true,
    sortable: true,
    render: (skill) => (
      <Badge tone={skill.visibility === 'public' ? 'info' : 'neutral'}>
        {skill.visibility}
      </Badge>
    ),
  },
  {
    key: 'flags',
    header: 'Flags',
    hideBelow: 'mobile',
    nowrap: true,
    render: (skill) => <SkillFlags skill={skill} />,
  },
  {
    key: 'usage',
    header: 'Usage (14d)',
    nowrap: true,
    render: (skill) => {
      const total = skill.usage_total_14d ?? 0;

      return (
        <Tooltip content={`${total} messages over 14 days`}>
          <span className='flex items-center gap-2'>
            <Sparkline
              values={skill.usage_sparkline ?? []}
              tone='success'
              ariaLabel={`Usage over 14 days for ${skill.name}`}
            />
            <span className='text-xs text-ink-muted tabular-nums'>{total}</span>
          </span>
        </Tooltip>
      );
    },
  },
];

/**
 * The skill catalog: every skill the agent can load, and how much each is
 * actually being used.
 *
 * A row opens the skill's detail page; editing and deleting stay in the row
 * menu, so a mis-aimed click reads rather than writes.
 */
export function SkillsPage() {
  const router = useRouter();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<DataTableSort>({ key: 'name', direction: 'asc' });
  const [deleting, setDeleting] = useState<Skill | null>(null);
  const [deleteError, setDeleteError] = useState('');

  const skills = useQuery({
    queryKey: ['skills', 'list', page, sort],
    queryFn: () => listSkills(page, sort.key, sort.direction),
    /* The rows stay put while the next page lands; only the numbers change. */
    placeholderData: keepPreviousData,
  });

  const remove = useMutation({ mutationFn: (skill: Skill) => deleteSkill(skill.id) });

  /*
   * The dialog stays open on a failure and says so inside itself: closing it
   * would take the only report of what went wrong with it.
   */
  async function confirmDelete() {
    if (!deleting) return;

    setDeleteError('');
    try {
      await remove.mutateAsync(deleting);
      setDeleting(null);
      await queryClient.invalidateQueries({ queryKey: ['skills'] });
    } catch (error) {
      reportError(error, { where: 'skills.delete', skillId: deleting.id });
      setDeleteError(apiErrorMessage(error, 'Failed to delete skill'));
    }
  }

  const rows = skills.data?.data ?? [];
  const total = skills.data?.meta.totalItems ?? 0;

  return (
    <>
      <PageHeader title='Skills' />
      <PageBody>
        <DataTable
          label='Skills'
          columns={columns}
          rows={rows}
          rowKey={(skill) => skill.id}
          loading={skills.isLoading}
          error={skills.isError ? 'Failed to load skills' : undefined}
          sort={sort}
          onSortChange={(next) => {
            setSort(next);
            setPage(1);
          }}
          pagination={{ page, pageSize: PAGE_SIZE, total }}
          onPageChange={setPage}
          rowHref={(skill) => `/skills/${skill.id}`}
          onNavigate={(href) => void router.navigate({ href })}
          emptyState={
            <EmptyState
              title='No skills yet'
              description='A skill is a prompt, a set of tools, and the rules for who gets it.'
            />
          }
          toolbar={
            <Button
              size='sm'
              onClick={() => void router.navigate({ to: '/skills/create' })}
            >
              <Plus className='size-4' aria-hidden='true' />
              Create skill
            </Button>
          }
          actions={(skill) => (
            <TableActionsMenu
              label={`Actions for ${skill.name}`}
              items={[
                {
                  label: 'Edit',
                  icon: <Pencil className='size-4' aria-hidden='true' />,
                  onSelect: () =>
                    void router.navigate({
                      to: '/skills/$id/edit',
                      params: { id: skill.id },
                    }),
                },
                {
                  label: 'Delete',
                  icon: <Trash2 className='size-4' aria-hidden='true' />,
                  tone: 'danger',
                  onSelect: () => {
                    setDeleteError('');
                    setDeleting(skill);
                  },
                },
              ]}
            />
          )}
        />
      </PageBody>

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        title='Delete skill?'
        description={`Delete skill "${deleting?.name ?? ''}"? This cannot be undone. Users currently enrolled in this skill will have their enrollment removed.`}
        confirmLabel='Delete'
        cancelLabel='Cancel'
        variant='danger'
        error={deleteError}
        onConfirm={confirmDelete}
      />
    </>
  );
}
